import * as ng from 'angular';

let PasswordStrengthServiceFactory: Function = () => {
    return new PasswordStrengthService();
};

let PasswordStrength = {
    name: "PasswordStrength",
    factory: PasswordStrengthServiceFactory
};

interface IPasswordStrengthService {
    measureStrength(p): number;
}

enum ConsecutiveModeEnum {
    None = 0,
    UppercaseLetter = 1,
    LowercaseLetter = 2,
    Digit = 3
};

export class PasswordStrengthService implements IPasswordStrengthService {

    constructor() {

    }

    //Note: The algorithm here needs to be kept in sync with the algorithm in PaswordAdvisor.cs
    //The basis of this algorithm and further reading can be found in that file.
    //If changes need to be made here, they MUST be incorporated into PaswordAdvisor.cs as well.
    //This implementation may not be the most correct "typescript" way to do this,
    //but it is intentionally done this way to make it mirror the server-side implentation as much as possible to make maintenance easier
    public measureStrength(p:string): number {
        const SequentialLetters: string  = "abcdefghijklmnopqrstuvwxyz";
        const UppercaseLetters: string = SequentialLetters.toUpperCase();
        const SequentialDigits: string = "01234567890";
        const SequentialCharacterThreshold: number = 3;

        let stringReverse = function (str: string): string {
            for (var i = str.length - 1, out = ''; i >= 0; out += str[i--]) { }
            return out;
        };
       
        let isOneChar = function (str: string): boolean { return str.length == 1;};

        let isDigit = function (char: string): boolean {
            if(!isOneChar(char))
                return false;

            return SequentialDigits.includes(char);
        };

        let isLower = function (char: string): boolean {
            if(!isOneChar(char))
                return false;

            return SequentialLetters.includes(char);
        };

        let isUpper = function (char: string): boolean {
            if(!isOneChar(char))
                return false;

            return UppercaseLetters.includes(char);
        };

        let isSymbol = function (char: string): boolean {
            //this isn't strictly necessary given the calls to the other functions, but this makes the function behavior more conistent 
            //with the other isX functions defined above
            if(!isOneChar(char))
                return false;

            return !(isDigit(char) || isLower(char) || isUpper(char))
        };

        let strength: number = 0;

        if (p) {
            let uniqueCharacters = new Set();
            let uppercaseLetterCount: number = 0;
            let lowercaseLetterCount: number = 0;
            let digitCount: number = 0;
            let symbolCount: number = 0;
            let middleDigitSymbolCount: number = 0;
            let consecutiveMode: ConsecutiveModeEnum = ConsecutiveModeEnum.None;
            let consecutiveUppercaseLetterCount: number = 0;
            let consecutiveLowercaseLetterCount: number = 0;
            let consecutiveDigitCount: number = 0;
            let repeatedCharacterPenalty: number = 0.0;
            let repeatedCharactersCount: number = 0;
            let sequentialDigitStringsCount: number = 0;
            let sequentialAlphaStringsCount: number = 0;

            for (let i : number = 0; i < p.length; i++) {
                uniqueCharacters.add(p[i]);
            }
            
            let uniqueCharactersCount: number = uniqueCharacters.size;

            for (let index : number = 0; index < p.length; index++) {
                let character: string = p[index];
                let isRepeatedCharacter: boolean = false;

                                // Count digits
                if (isDigit(character))
                {
                    digitCount++;

                    if (consecutiveMode == ConsecutiveModeEnum.Digit)
                        consecutiveDigitCount++;
                    consecutiveMode = ConsecutiveModeEnum.Digit;

                    //If not the first or last character, then it's considered a "middle" character by the algorithm
                    if (index > 0 && index < p.length - 1)
                        middleDigitSymbolCount++;
                }

                // Count uppercase characters
                if (isUpper(character))
                {
                    uppercaseLetterCount++;
                    if (consecutiveMode == ConsecutiveModeEnum.UppercaseLetter)
                        consecutiveUppercaseLetterCount++;
                    consecutiveMode = ConsecutiveModeEnum.UppercaseLetter;
                }

                // Count lowercase characters
                if (isLower(character))
                {
                    lowercaseLetterCount++;
                    if (consecutiveMode == ConsecutiveModeEnum.LowercaseLetter)
                        consecutiveLowercaseLetterCount++;
                    consecutiveMode = ConsecutiveModeEnum.LowercaseLetter;
                }

                // Count symbols
                if (isSymbol(character))
                {
                    symbolCount++;
                    consecutiveMode = ConsecutiveModeEnum.None;

                    //If not the first or last character, then it's considered a "middle" character by the algorithm
                    if (index > 0 && index < p.length - 1)
                        middleDigitSymbolCount++;
                }

                for (var repeatIndex = 0; repeatIndex < p.length; repeatIndex++)
                {
                    if (p[index] == p[repeatIndex] && index != repeatIndex)
                    {
                        isRepeatedCharacter = true;
                        //repeated characters that are further away from each other in the password should provide less of a penalty than ones which are closer to each other in the password
                        repeatedCharacterPenalty += Math.abs(p.length / (index - repeatIndex));
                    }
                }

                if (isRepeatedCharacter)
                {
                    repeatedCharactersCount++;
                    //In the event that there are unique characters in the password, then decrease the repeated character penalty
                    //This means that a password containing the string "food" would incur less of a repeated character penalty than the string "oooo".
                    //NOTE: NRM I changed the way this works from the sample implementation because what it considered the number of unique characters is actually the difference between the password length and the number of 
                    //      duplicate characters encountered so far (repeatedCharacterCount). This value of uniqueCharsCount will change through the iterations over the password.
                    //      As there is no reference to why it was done they way it was in the source, I decided to simplify the repeated character penalty calculation.
                    //      This method is still more robust than the previous calculation done in this code.
                    repeatedCharacterPenalty = uniqueCharactersCount > 0 ? Math.ceil(repeatedCharacterPenalty / uniqueCharactersCount) : Math.ceil(repeatedCharacterPenalty);
                }

            }

            // Check for sequential alpha string patterns (forward and reverse) 
            for (let startPos:number = 0; startPos < SequentialLetters.length - SequentialCharacterThreshold; startPos++)
            {
                let forwardSequentialCharacters:string = SequentialLetters.substr(startPos, SequentialCharacterThreshold);
                let reverseSequentialCharacters:string = stringReverse(forwardSequentialCharacters);
                if (p.includes(forwardSequentialCharacters) || p.includes(reverseSequentialCharacters))
                {
                    sequentialAlphaStringsCount++;
                }
            }

            // Check for sequential numeric string patterns (forward and reverse)
            for (let startPos:number = 0; startPos < SequentialDigits.length - SequentialCharacterThreshold; startPos++)
            {
                let forwardSequentialCharacters:string = SequentialDigits.substr(startPos, SequentialCharacterThreshold);
                let reverseSequentialCharacters:string = stringReverse(forwardSequentialCharacters);
                if (p.includes(forwardSequentialCharacters) || p.includes(reverseSequentialCharacters))
                {
                    sequentialDigitStringsCount++;
                }
            }

                        // Password Length
            strength = 4 * p.length;

            // Uppercase letters
            if (uppercaseLetterCount > 0) strength += ((p.length - uppercaseLetterCount) * 2);

            // Lowercase letters
            if (lowercaseLetterCount > 0) strength += ((p.length - lowercaseLetterCount) * 2);

            // Digits
            strength += (digitCount * 4);

            // Symbols
            strength += (symbolCount * 6);

            // Score += (Number of digits or symbols in middle of password *2)
            strength += (middleDigitSymbolCount * 2);

            // Requirements
            let requirements:number = 0;
            if (p.length >= 8) requirements++;     // Min password length
            if (uppercaseLetterCount > 0) requirements++;      // Uppercase letters
            if (lowercaseLetterCount > 0) requirements++;      // Lowercase letters
            if (digitCount > 0) requirements++;          // Digits
            if (symbolCount > 0) requirements++;         // Symbols

            // If we have more than 3 requirements increase the score
            if (requirements > 3) strength += (requirements * 2);


            // Deductions 

            // Only letters
            if (digitCount == 0 && symbolCount == 0) 
                strength -= p.length;

            // Only Digits
            if (digitCount == p.length) 
                strength -= p.length;

            // Repeated Characters
            if (repeatedCharactersCount > 1) 
                strength -= repeatedCharacterPenalty;

            // Consecutive uppercase letters
            strength -= (consecutiveUppercaseLetterCount * 2);

            // Consecutive lowercase letters
            strength -= (consecutiveLowercaseLetterCount * 2);

            // Consecutive digits
            strength -= (consecutiveDigitCount * 2);

            // Password contains sequences of letters
            strength -= (sequentialAlphaStringsCount * 3);

            // Password contains sequences of digits
            strength -= (sequentialDigitStringsCount * 3);

        }

        //this normalization to a "percentage" is not done on the server-side, 
        //but it is done here because there is conditional text displayed on the screen based on thresholds
        return Math.max(0, Math.min(100, Math.round(strength)));
    }

}

export { PasswordStrength, IPasswordStrengthService };
export default PasswordStrength;



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./app/components/security/services/password-strength.ts